/* eslint-disable react/require-default-props */
/* eslint-disable no-underscore-dangle */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaBoxOpen, FaSlidersH } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { get } from 'lodash';

import axios from '../../services/axios';
import {
   createMovement,
   readMovementApiError,
} from '../../services/movements/movementsApi';
import { notifyStockWarnings } from '../../utils/movementFeedback';
import Loading from '../../components/Loading';
import DuplicateMovementsModal, {
   DuplicateCheckHint,
} from '../../components/DuplicateMovementsModal';
import StockImpactModal, {
   StockImpactHint,
} from '../../components/StockImpactModal';
import MovementConfirmModal from '../../components/MovementConfirmModal';
import MovementItemsEditor from '../../components/movements/MovementItemsEditor';
import MovementSummaryAside from '../../components/movements/MovementSummaryAside';
import {
   DOC_TYPE_OPTIONS_IN,
   INTEGER_RE,
   sumItemUnits,
} from '../../components/movements/constants';
import { useMovementDuplicateCheck } from '../../hooks/useMovementDuplicateCheck';
import { useStockImpactCheck } from '../../hooks/useStockImpactCheck';
import { useUserPdvs } from '../../hooks/useUserPdvs';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   ComposerPageGrid,
   Field,
   FieldHint,
   FieldLabel,
   FieldSelect,
   FieldTextarea,
   FieldInput,
   FormCard,
   FormCardTop,
   FormGrid,
   Section,
   SectionHead,
   SectionHeadRight,
   SectionIcon,
   SectionTitles,
} from '../../styles/ui/Composer';
import { GhostOnDarkButton } from '../../styles/ui/Button';

// ===========================================================================
// Entrada de produtos (`POST /movements`, ou `POST /movements/xml` quando o
// fluxo vem do importador de NF-e com `doc_key`).
//
// Redesenho 2026-09-12 (FC7 cap.2 §H.3): a tela virou COMPOSER — card
// seccionado (1. Parâmetros · 2. Itens) com cartão lateral de resumo,
// checklist de pendências e as ações. A lista de itens e o cartão lateral são
// componentes compartilhados com a saída e com o editor.
//
// Decisão do Caio (2026-09-12): **não existe seletor Entrada|Saída aqui**. O
// protótipo fundia as duas telas num toggle; o tipo do movimento continua
// definido pela ROTA (e pela entrada de menu), justamente para ninguém trocar
// o tipo com o formulário preenchido.
//
// Comportamento preservado 1:1: prefill do fluxo XML, warn guard de
// duplicidade (0089), warn guard de saldo negativo (0093 §5a), modal único de
// confirmação e agregação por `prod_id` no submit.
// ===========================================================================

export default function MovementIn({ prefill = null }) {
   const userId = useSelector((state) => state.auth.user.id);
   const navigate = useNavigate();
   const location = useLocation();

   const { pdvs, isLoading: loadingPdvs } = useUserPdvs();

   // params
   const [pdvId, setPdvId] = useState('');
   const [docType, setDocType] = useState('');
   const [docNumber, setDocNumber] = useState('');
   const [docKey, setDocKey] = useState('');
   const [description, setDescription] = useState('');

   const [productList, setProductList] = useState([]);
   const [paramList, setParamList] = useState({});

   const [isLoading, setIsLoading] = useState(false);
   // Remonta o editor de itens ao limpar a tela: os campos de busca/quantidade
   // da linha de adição são estado INTERNO dele, e um produto escolhido e não
   // adicionado sobreviveria ao "Limpar".
   const [itemsEditorKey, setItemsEditorKey] = useState(0);

   // Modal unificado de confirmação do Salvar: null = fechado; aberto guarda
   // os achados FRESCOS dos dois warn guards.
   const [confirmState, setConfirmState] = useState(null);

   // Warn guard de duplicidade (handoff 0089) — informativo, nunca bloqueia.
   const duplicateCheck = useMovementDuplicateCheck({
      pdvId,
      docType,
      docNumber,
      moveType: 'in',
   });

   // Gatilho "cedo" na troca dos selects: só quando o conjunto já está
   // completo. O nº documento dispara no blur (não a cada tecla).
   useEffect(() => {
      if (pdvId && docType && docNumber) duplicateCheck.triggerCheck();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [pdvId, docType]);

   // Warn guard de saldo negativo (handoff 0093) — informativo, nunca bloqueia.
   // Numa entrada só dispara aviso se o produto já estiver negativo e seguir
   // negativo; o backend é a fonte da verdade do cálculo.
   const stockImpact = useStockImpactCheck({
      pdvId,
      moveType: 'in',
      items: productList,
   });

   // Gatilho "cedo" do impacto: re-checa quando a lista de itens/PDV muda
   // (ações explícitas — o hook cacheia e deduplica; alimenta só o indicador).
   useEffect(() => {
      stockImpact.triggerCheck();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [productList, pdvId]);

   // evita re-aplicar prefill repetidamente (modal pode abrir/fechar sem desmontar)
   const appliedPrefillRef = useRef('');

   // Prefill vindo do XML (produtos + doc + descrição) — falta só PDV e salvar
   useEffect(() => {
      if (!prefill) return;

      const key =
         `${prefill?.doc_key || ''}|${prefill?.doc_number || ''}|` +
         `${(prefill?.items || []).map((it) => `${it.prod_id}`).join(',')}`;

      if (appliedPrefillRef.current === key) return;

      const allowedDocTypes = DOC_TYPE_OPTIONS_IN.map((option) => option.value);

      const nextDocType = allowedDocTypes.includes(
         String(prefill?.doc_type || '').toUpperCase()
      )
         ? String(prefill.doc_type).toUpperCase()
         : 'NOTA FISCAL';

      setDocType(nextDocType);
      setDocNumber(String(prefill?.doc_number ?? ''));
      setDocKey(String(prefill?.doc_key ?? ''));
      setDescription(String(prefill?.movement_description ?? '').toUpperCase());

      const mapped = (prefill?.items || []).map((it, idx) => ({
         prod_id: Number(it.prod_id),
         prod_qtd: Number(it.prod_qtd ?? it.quantity ?? 0),
         move_type: 'in',
         pdv_id: Number.parseInt(pdvId, 10),

         unit_cost: it.unit_cost ?? null,
         total_cost: it.total_cost ?? null,

         sku: it.sku || it.prod_sku || it.prodSku || '',
         prodName: it.prodName || it.prod_name || it.xml_name || '',
         xml_name: it.xml_name || it.name || '',
         _prefillIndex: idx,
      }));

      setProductList(mapped);

      appliedPrefillRef.current = key;
   }, [prefill, pdvId]);

   useEffect(() => {
      const params = {
         pdv_id: pdvId,
         move_type: 'in',
         doc_type: docType,
         doc_number: docNumber,
         movement_description: description,
         user_id: userId,
      };

      // só inclui doc_key se existir (para /movements/xml)
      if (docKey) params.doc_key = docKey;

      setParamList(params);
   }, [docNumber, docType, pdvId, description, userId, docKey]);

   const clearFields = () => {
      setPdvId('');
      setDocType('');
      setDocNumber('');
      setDocKey('');
      setDescription('');
      setProductList([]);
      setParamList({});

      duplicateCheck.reset();
      stockImpact.reset();
      setConfirmState(null);
      setItemsEditorKey((key) => key + 1);

      // permite aplicar novamente caso o mesmo prefill venha depois
      appliedPrefillRef.current = '';
   };

   const handleClearFields = () => {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
         'Limpar todos os campos e itens desta entrada?'
      );
      if (!ok) return;
      clearFields();
   };

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/movements');
   };

   const handleSubmitEntry = async () => {
      if (
         !paramList.pdv_id ||
         !paramList.move_type ||
         !paramList.doc_type ||
         !paramList.doc_number ||
         !paramList.user_id ||
         productList.length === 0
      ) {
         toast.error('Campos obrigatórios vazios.');
         return;
      }

      // Se houver doc_key, entende que é importação XML e usa endpoint próprio
      // (shape antigo — o handoff 0093 só migrou o POST /movements).
      const isXmlFlow = !!docKey;

      try {
         setIsLoading(true);

         const groupedItems = Object.values(
            productList.reduce((acc, item) => {
               const key = item.prod_id;
               if (!acc[key]) {
                  acc[key] = {
                     ...item,
                     prod_qtd: Number(item.prod_qtd) || 0,
                     pdv_id: Number.parseInt(pdvId, 10),
                     move_type: 'in',
                  };
               } else {
                  acc[key].prod_qtd =
                     Number(acc[key].prod_qtd) + (Number(item.prod_qtd) || 0);
               }
               return acc;
            }, {})
         );

         if (isXmlFlow) {
            const movementData = {
               movement: paramList,
               items: groupedItems,
               // opcional: backend ignora se não existir, mas é útil p/ controlar política
               options: { update_cost_price: true },
            };

            const response = await axios.post('/movements/xml', movementData);

            const createdMovementId =
               get(response, 'data.data.newMovement.id', null) ??
               get(response, 'data.newMovement.id', null); // fallback

            const successMsg = get(
               response,
               'data.message',
               get(
                  response,
                  'data.successCreationMsg',
                  'Movimento registrado com sucesso.'
               )
            );

            toast.success(successMsg);

            if (createdMovementId) {
               navigate(`/movements/${createdMovementId}`);
            } else {
               toast.warn('Movimento salvo, mas não foi possível obter o ID.');
            }
            return;
         }

         // JSend Geração 3 (handoff 0093): sucesso desembrulhado no service.
         const { movement, stockWarnings, message } = await createMovement({
            movement: paramList,
            items: groupedItems,
         });

         toast.success(message);
         notifyStockWarnings(stockWarnings);

         if (movement?.id) {
            navigate(`/movements/${movement.id}`);
         } else {
            toast.warn('Movimento salvo, mas não foi possível obter o ID.');
         }
      } catch (err) {
         const status = get(err, 'response.status', 0);

         if (isXmlFlow && status === 409) {
            toast.error(
               'Este XML já foi importado anteriormente (doc_key duplicado).'
            );
            return;
         }

         const apiError = readMovementApiError(err);
         Object.entries(apiError.fieldErrors).forEach(([field, msg]) =>
            toast.error(`${field}: ${msg}`)
         );
         if (!Object.keys(apiError.fieldErrors).length) {
            toast.error(apiError.message);
         }
      } finally {
         setIsLoading(false);
      }
   };

   // Guarda do Salvar: consulta os dois warn guards FRESCOS (em paralelo) e
   // abre o modal unificado de confirmação — que substitui o window.confirm
   // e exibe os achados sem nunca bloquear.
   const handleConfirmRegister = async () => {
      const [duplicates, negativeStock] = await Promise.all([
         duplicateCheck.checkNow(),
         stockImpact.checkNow(),
      ]);
      setConfirmState({ duplicates, negativeStock });
   };

   const handleConfirmModalConfirm = () => {
      setConfirmState(null);
      handleSubmitEntry();
   };

   // Só é verdade no instante em que o campo está vazio COM O FOCO DENTRO: o
   // stepper normaliza para 1 no blur. Serve de rede de segurança contra a
   // corrida "apaga a quantidade e clica em Salvar" — sem ela, `Number('')`
   // viraria 0 no payload.
   const hasInvalidQty = productList.some(
      (item) =>
         String(item.prod_qtd ?? '').trim() === '' || Number(item.prod_qtd) < 1
   );

   const hasParams = !!pdvId && !!docType && !!docNumber;
   const canSave =
      hasParams &&
      productList.length > 0 &&
      !hasInvalidQty &&
      (!docKey || docKey.length >= 10);

   const lockDocFields = !!prefill; // no XML: doc fields já vêm prontas
   const pdvName = pdvs.find((pdv) => String(pdv.id) === String(pdvId))
      ?.pdv_name;
   const totalUnits = sumItemUnits(productList);

   return (
      <Container>
         <Loading isLoading={isLoading || loadingPdvs} />

         {/* Sem header no modo EMBUTIDO (fluxo XML monta esta tela dentro de
             um Modal): o PageHeader é branco-sobre-escuro do shell e, sobre o
             fundo claro do modal, o título simplesmente desaparecia — quirk
             que já existia antes do redesenho. O contexto ali vem do próprio
             modal, e o "Voltar" não faria sentido. */}
         {!prefill && (
            <PageHeader>
               <PageHeaderText>
                  <PageTitle>Nova entrada</PageTitle>
                  <PageSubtitle>
                     Registre a entrada de produtos no estoque de um PDV.
                  </PageSubtitle>
               </PageHeaderText>

               <PageHeaderActions>
                  <GhostOnDarkButton type="button" onClick={handleBack}>
                     ← Voltar
                  </GhostOnDarkButton>
               </PageHeaderActions>
            </PageHeader>
         )}

         <ComposerPageGrid>
            <FormCard>
               <FormCardTop>
                  <h2>Entrada de produtos</h2>
                  <p>
                     Informe o PDV e o documento de origem, monte a lista de
                     itens e confirme — o saldo do PDV é atualizado no registro.
                  </p>
               </FormCardTop>

               <Section>
                  <SectionHead>
                     <SectionIcon>
                        <FaSlidersH size={15} aria-hidden="true" />
                        <span className="step">1</span>
                     </SectionIcon>
                     <SectionTitles>
                        <div className="t">Parâmetros</div>
                        <div className="d">
                           Define onde e por qual documento o estoque será
                           movimentado.
                        </div>
                     </SectionTitles>
                  </SectionHead>

                  <FormGrid>
                     <Field $span={4}>
                        <FieldLabel htmlFor="movement-in-pdv">
                           PDV<span className="req">*</span>
                        </FieldLabel>
                        <FieldSelect
                           id="movement-in-pdv"
                           value={pdvId}
                           onChange={(event) => setPdvId(event.target.value)}
                           disabled={isLoading || loadingPdvs}
                        >
                           <option value="">Selecione um PDV</option>
                           {pdvs.map((pdv) => (
                              <option key={pdv.id} value={pdv.id}>
                                 {pdv.pdv_name}
                              </option>
                           ))}
                        </FieldSelect>
                     </Field>

                     <Field $span={4}>
                        <FieldLabel htmlFor="movement-in-doc-type">
                           Documento de entrada<span className="req">*</span>
                        </FieldLabel>
                        <FieldSelect
                           id="movement-in-doc-type"
                           value={docType}
                           onChange={(event) => setDocType(event.target.value)}
                           disabled={isLoading || lockDocFields}
                        >
                           <option value="" disabled>
                              Selecione o tipo de documento
                           </option>
                           {DOC_TYPE_OPTIONS_IN.map((option) => (
                              <option key={option.value} value={option.value}>
                                 {option.label}
                              </option>
                           ))}
                        </FieldSelect>
                     </Field>

                     <Field $span={4}>
                        <FieldLabel htmlFor="movement-in-doc-number">
                           Nº do documento<span className="req">*</span>
                        </FieldLabel>
                        <FieldInput
                           id="movement-in-doc-number"
                           type="text"
                           inputMode="numeric"
                           value={docNumber}
                           disabled={isLoading || lockDocFields}
                           placeholder="Ex.: 424242"
                           onChange={(event) => {
                              const { value } = event.target;
                              if (value === '' || INTEGER_RE.test(value)) {
                                 setDocNumber(value);
                              }
                           }}
                           onBlur={duplicateCheck.triggerCheck}
                        />
                        <DuplicateCheckHint
                           status={duplicateCheck.status}
                           count={duplicateCheck.duplicates.length}
                           onOpen={duplicateCheck.reopenModal}
                        />
                     </Field>

                     {/* aparece apenas quando for fluxo XML */}
                     {docKey ? (
                        <Field $span={12}>
                           <FieldLabel htmlFor="movement-in-doc-key">
                              Chave de acesso (XML)
                           </FieldLabel>
                           <FieldInput
                              id="movement-in-doc-key"
                              type="text"
                              value={docKey}
                              disabled
                           />
                           <FieldHint>
                              Movimento importado de XML: o registro espelha o
                              documento fiscal e não poderá ser editado depois.
                           </FieldHint>
                        </Field>
                     ) : null}

                     <Field $span={12}>
                        <FieldLabel htmlFor="movement-in-description">
                           Descrição
                        </FieldLabel>
                        <FieldTextarea
                           id="movement-in-description"
                           value={description}
                           disabled={isLoading || lockDocFields}
                           onChange={(event) =>
                              setDescription(event.target.value.toUpperCase())
                           }
                           placeholder="Ex.: Entrada por NF / transferência / ajuste..."
                        />
                     </Field>
                  </FormGrid>
               </Section>

               <Section>
                  <SectionHead>
                     <SectionIcon>
                        <FaBoxOpen size={15} aria-hidden="true" />
                        <span className="step">2</span>
                     </SectionIcon>
                     <SectionTitles>
                        <div className="t">Itens</div>
                        <div className="d">
                           {productList.length
                              ? `${productList.length} ${
                                   productList.length === 1 ? 'item' : 'itens'
                                } · ${totalUnits} un.`
                              : 'Bipe o código ou busque pelo nome do produto.'}
                        </div>
                     </SectionTitles>
                     <SectionHeadRight>
                        <StockImpactHint
                           status={stockImpact.status}
                           count={stockImpact.negativeStock.length}
                           onOpen={stockImpact.reopenModal}
                           onRefresh={stockImpact.triggerCheck}
                        />
                     </SectionHeadRight>
                  </SectionHead>

                  <MovementItemsEditor
                     key={itemsEditorKey}
                     items={productList}
                     onChange={setProductList}
                     disabled={isLoading}
                     pdvId={pdvId}
                     emptyDescription="Selecione o PDV, bipe o SKU (ou busque pelo nome) e informe a quantidade."
                  />
               </Section>
            </FormCard>

            <MovementSummaryAside
               typeLabel="Entrada"
               typeVariant="green"
               rows={[
                  { label: 'Tipo', value: 'Entrada de produtos' },
                  { label: 'PDV', value: pdvName },
                  {
                     label: 'Documento',
                     value: [docType, docNumber && `nº ${docNumber}`]
                        .filter(Boolean)
                        .join(' '),
                  },
                  { label: 'Itens', value: productList.length || '0' },
                  { label: 'Quantidade total', value: `${totalUnits} un.` },
               ]}
               /* Quantidade NÃO é item de checklist (2026-09-12): com a lista
                  vazia ele ficava verde por vacuidade — "preenchidas" sem nada
                  para preencher. O stepper normaliza o campo no blur, então
                  item na lista sempre tem quantidade válida; o guard abaixo é
                  só rede de segurança do instante em que o campo está vazio
                  com o foco dentro. */
               checklist={[
                  { label: 'PDV e documento informados', ok: hasParams },
                  {
                     label: 'Pelo menos um item na lista',
                     ok: productList.length > 0,
                  },
               ]}
               primaryLabel="Salvar entrada"
               onPrimary={handleConfirmRegister}
               primaryDisabled={isLoading || !canSave}
               primaryTitle={
                  hasInvalidQty
                     ? 'Há item com a quantidade em branco — conclua a edição para salvar'
                     : undefined
               }
               secondaryLabel="Limpar"
               onSecondary={handleClearFields}
               secondaryDisabled={isLoading}
               note="Saldo negativo e documento duplicado geram aviso na confirmação — nunca bloqueiam o registro."
            />
         </ComposerPageGrid>

         <DuplicateMovementsModal
            isOpen={duplicateCheck.isModalOpen}
            duplicates={duplicateCheck.duplicates}
            queryInfo={{ pdvName, docType, docNumber }}
            onClose={duplicateCheck.closeModal}
         />

         <StockImpactModal
            isOpen={stockImpact.isModalOpen}
            negativeStock={stockImpact.negativeStock}
            queryInfo={{ pdvName, moveTypeLabel: 'Entrada' }}
            onClose={stockImpact.closeModal}
         />

         <MovementConfirmModal
            isOpen={!!confirmState}
            subtitle={[
               pdvName,
               'Entrada',
               docType,
               docNumber ? `nº ${docNumber}` : '',
            ]
               .filter(Boolean)
               .join(' · ')}
            itemsCount={productList.length}
            duplicates={confirmState?.duplicates ?? []}
            negativeStock={confirmState?.negativeStock ?? []}
            onCancel={() => setConfirmState(null)}
            onConfirm={handleConfirmModalConfirm}
         />
      </Container>
   );
}

MovementIn.propTypes = {
   prefill: PropTypes.shape({
      doc_key: PropTypes.string,
      doc_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      doc_type: PropTypes.string,
      movement_description: PropTypes.string,
      items: PropTypes.arrayOf(
         PropTypes.shape({
            prod_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            prod_qtd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            unit_cost: PropTypes.number,
            total_cost: PropTypes.number,
            sku: PropTypes.string,
            prod_name: PropTypes.string,
            prodName: PropTypes.string,
            xml_name: PropTypes.string,
         })
      ),
   }),
};
